import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import TicketUpdatePreferencesModal from "../../../ConfirmShow/components/TicketUpdatePreferencesModal";

/* ponytail: data-viz avatar palette, no sl token */
const AVATAR_COLORS = ["#98c648", "#1982C4", "#5f5383", "#f79256"];

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// The dashboard serializes contacts in camelCase; the shared modal reads the
// legacy snake_case calendar_event_contact shape, so adapt at the boundary.
const toModalContact = (c) => ({
  id: c.id,
  send_daily_ticket_updates: c.sendDailyTicketUpdates,
  ticket_update_frequency: c.ticketUpdateFrequency,
  ticket_update_days: c.ticketUpdateDays,
  ticket_update_hours: c.ticketUpdateHours ?? [c.ticketUpdateHour].filter(hour => hour != null),
  venue_time_zone: c.venueTimeZone,
});

const KeyContactsCard = ({ contacts, teamId, csrfToken, onContactsChange }) => {
  const [prefsContact, setPrefsContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const modalContact = useMemo(() => (prefsContact ? toModalContact(prefsContact) : null), [prefsContact]);

  const handleSave = (params) => {
    if (!prefsContact) return;
    setSaving(true);
    axios
      .patch(
        `/teams/${teamId}/calendar_event_contacts/${prefsContact.id}`,
        { calendar_event_contact: params },
        { headers: { "X-CSRF-TOKEN": csrfToken } }
      )
      .then(() => {
        if (onContactsChange) {
          onContactsChange(contacts.map((c) => (c.id === prefsContact.id
            ? {
                ...c,
                sendDailyTicketUpdates: params.send_daily_ticket_updates,
                ticketUpdateFrequency: params.ticket_update_frequency,
                ticketUpdateDays: params.ticket_update_days,
                ticketUpdateHours: params.ticket_update_hours,
              }
            : c)));
        }
        setPrefsContact(null);
      })
      .catch(() => toast.error("Failed to save settings. Please try again."))
      .finally(() => setSaving(false));
  };

  return (
    <div className="d2-card">
      <div className="d2-card-header">
        <span className="d2-card-title">Key Contacts</span>
        <a className="d2-card-link" href="#contacts">All Contacts <i className="fas fa-arrow-right" style={{ fontSize: "10px" }} /></a>
      </div>

      {contacts.length === 0 ? (
        <div className="contact-role">No contacts yet.</div>
      ) : (
        contacts.map((c, idx) => (
          <div className="contact-row" key={c.id}>
            <div className="contact-avatar" style={{ background: AVATAR_COLORS[idx % AVATAR_COLORS.length] }}>
              {initials(c.name)}
            </div>
            <div className="contact-info">
              <div className="contact-name">
                {c.agentPermalink
                  ? <a href={`/agents/${c.agentPermalink}`}>{c.name || "Unnamed"}</a>
                  : (c.name || "Unnamed")}
              </div>
              {(c.contactType || c.company) && (
                <div className="contact-role">{[c.contactType, c.company].filter(Boolean).join(" · ")}</div>
              )}
            </div>
            {(c.email || c.phone) && (
              <div className="contact-actions">
                {c.email && (
                  <button
                    type="button"
                    className={`contact-action-btn${c.sendDailyTicketUpdates ? " contact-action-btn--active" : ""}`}
                    onClick={() => setPrefsContact(c)}
                    title="Ticket count email settings"
                    aria-label={`Ticket count email settings for ${c.name || "contact"}`}
                  >
                    <i className="far fa-envelope" />
                  </button>
                )}
                {c.phone && <a href={`tel:${c.phone}`} className="contact-action-btn" aria-label={`Call ${c.name || "contact"}`}><i className="fas fa-phone" /></a>}
              </div>
            )}
          </div>
        ))
      )}

      <TicketUpdatePreferencesModal
        open={prefsContact !== null}
        onClose={() => setPrefsContact(null)}
        calendarEventContact={modalContact}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
};

KeyContactsCard.propTypes = {
  contacts: PropTypes.array.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  csrfToken: PropTypes.string,
  onContactsChange: PropTypes.func,
};

export default KeyContactsCard;
